import type { CellUploadInput } from '@components/CustomAllocationEditor/cellStates';
import { validateCsvFileType } from '@utils/csvFileValidation';
import { useToast } from 'hephaestus/web';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';
import {
  AccountAllocationCsvIssueCodes,
  hasBlockingIssue,
  issueOf,
  MAX_ACCOUNT_ALLOCATION_CSV_FILE_MB,
  type ParsedAccountAllocationCsv,
  parseAccountAllocationCsv,
} from './parseAccountAllocationCsv';
import type { AccountAllocationGridReport } from './types';

const BYTES_PER_MB = 1024 * 1024;

export type AccountAllocationCsvUploadProp = {
  report: AccountAllocationGridReport;
  // True when the cells were applied; a rejection has already toasted why.
  uploadCells: (cells: CellUploadInput[]) => boolean;
  displayFraction: (
    subAccountId: string,
    financialProductId: string,
    modelTarget: number,
  ) => number;
};

// Some systems report no MIME type for a .csv, so the extension stands in.
const isCsvFile = (file: File) =>
  validateCsvFileType(file) || (file.type === '' && /\.csv$/i.test(file.name));

const fileIssue = (file: File) => {
  if (!isCsvFile(file)) {
    return issueOf(AccountAllocationCsvIssueCodes.WRONG_FILE_TYPE);
  }
  if (file.size > MAX_ACCOUNT_ALLOCATION_CSV_FILE_MB * BYTES_PER_MB) {
    return issueOf(AccountAllocationCsvIssueCodes.FILE_TOO_LARGE);
  }
  return null;
};

const readText = (file: File) => file.text().catch(() => null);

export const useAccountAllocationCsvUpload = ({
  report,
  uploadCells,
  displayFraction,
}: AccountAllocationCsvUploadProp) => {
  const { t } = useTranslation();
  const { translateBackendString } = useLocalization();
  const { enqueueToast } = useToast();
  const [review, setReview] = useState<ParsedAccountAllocationCsv | null>(null);

  const apply = useCallback(
    (cells: CellUploadInput[]) => {
      if (cells.length === 0) {
        enqueueToast(
          t('components:accountAllocationGrid.csvUpload.nothingChanged'),
          { variant: 'info' },
        );
        return;
      }
      if (!uploadCells(cells)) return;
      enqueueToast(
        t('components:accountAllocationGrid.csvUpload.success', {
          count: cells.length,
        }),
        { variant: 'success' },
      );
    },
    [enqueueToast, t, uploadCells],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const issue = fileIssue(file);
      if (issue) {
        setReview({ cells: [], issues: [issue] });
        return;
      }

      const text = await readText(file);
      const parsed =
        text === null
          ? {
              cells: [],
              issues: [issueOf(AccountAllocationCsvIssueCodes.UNREADABLE_FILE)],
            }
          : parseAccountAllocationCsv(text, report, displayFraction, {
              t,
              translateBackendString,
            });

      if (parsed.issues.length > 0) {
        setReview(parsed);
        return;
      }
      apply(parsed.cells);
    },
    [apply, displayFraction, report, t, translateBackendString],
  );

  const importReviewed = useCallback(() => {
    if (review && !hasBlockingIssue(review.issues)) apply(review.cells);
    setReview(null);
  }, [apply, review]);

  const dismissReview = useCallback(() => setReview(null), []);

  return { review, uploadFile, importReviewed, dismissReview };
};
